import { useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '../components/ui/button.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import { Badge } from '../components/ui/badge.tsx';
import { Input } from '../components/ui/input.tsx';
import { Label } from '../components/ui/label.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.tsx';
import { DataTable } from '../components/data-table/data-table.tsx';
import { DataTableColumnHeader } from '../components/data-table/data-table-column-header.tsx';
import { type AuditEvent, type AuditLogsFilters, useAuditLogs } from '../hooks/useAuditLogs.ts';
import { useTableUrlState } from '../hooks/useTableUrlState.ts';
import { Download, FileJson, FileText, Filter, X } from 'lucide-react';
import { createLogger } from '@metron/shared';

const logger = createLogger('AuditLogsPage');

const formatTimestamp = (timestamp: string) => {
  return new Date(timestamp).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const getStatusBadge = (status: 'success' | 'failure') => {
  return status === 'success'
    ? <Badge variant='default'>Success</Badge>
    : <Badge variant='destructive'>Failure</Badge>;
};

export function AuditLogsPage() {
  const [filters, setFilters] = useState<AuditLogsFilters>({
    limit: 50,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [tempFilters, setTempFilters] = useState<AuditLogsFilters>({});

  const { events, isLoading, error, nextToken, refetch: _refetch, exportAuditLogs } = useAuditLogs(
    filters,
  );
  const [isExporting, setIsExporting] = useState(false);
  const tableState = useTableUrlState({
    defaultSorting: [{ id: 'timestamp', desc: true }],
  });

  const handleApplyFilters = () => {
    setFilters({ ...tempFilters, limit: 50 });
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    setTempFilters({});
    setFilters({ limit: 50 });
    setShowFilters(false);
  };

  const handleExport = async (format: 'csv' | 'json') => {
    setIsExporting(true);
    try {
      await exportAuditLogs(format, filters);
    } catch (err) {
      logger.error({ err }, 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const handleLoadMore = () => {
    if (nextToken) {
      setFilters({ ...filters, nextToken });
    }
  };

  const columns: ColumnDef<AuditEvent>[] = [
    {
      accessorKey: 'timestamp',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Timestamp' />,
      cell: ({ row }) => (
        <span className='text-xs whitespace-nowrap'>
          {formatTimestamp(row.getValue('timestamp'))}
        </span>
      ),
    },
    {
      accessorKey: 'username',
      header: ({ column }) => <DataTableColumnHeader column={column} title='User' />,
      cell: ({ row }) => (
        <div className='flex flex-col'>
          <div className='flex items-center gap-1'>
            <span className='text-sm font-medium'>{row.getValue('username')}</span>
            {row.original.impersonatedRole
              ? (
                <Badge variant='secondary' className='ml-1'>
                  as {row.original.impersonatedRole}
                </Badge>
              )
              : null}
          </div>
          <span className='text-xs text-muted-foreground'>
            {row.original.userGroups.join(', ')}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'action',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Action' />,
      cell: ({ row }) => (
        <Badge variant='outline' className='font-mono text-xs'>
          {row.getValue('action')}
        </Badge>
      ),
    },
    {
      accessorKey: 'resourceType',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Resource Type' />,
      cell: ({ row }) => (
        <span className='text-xs text-muted-foreground'>
          {row.getValue('resourceType')}
        </span>
      ),
    },
    {
      accessorKey: 'resource',
      header: 'Resource',
      cell: ({ row }) => (
        <span
          className='text-xs font-mono max-w-[200px] truncate block'
          title={row.getValue('resource')}
        >
          {row.getValue('resource')}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Status' />,
      cell: ({ row }) => getStatusBadge(row.getValue('status')),
    },
    {
      accessorKey: 'statusCode',
      header: 'Code',
      cell: ({ row }) => {
        const code = row.getValue('statusCode') as number | undefined;
        return code
          ? (
            <Badge variant='outline' className='font-mono text-xs'>
              {code}
            </Badge>
          )
          : null;
      },
    },
    {
      accessorKey: 'ipAddress',
      header: 'IP Address',
      cell: ({ row }) => <span className='text-xs font-mono'>{row.getValue('ipAddress')}</span>,
    },
    {
      id: 'error',
      header: 'Error',
      cell: ({ row }) => {
        const errorMessage = row.original.errorMessage;
        return errorMessage
          ? (
            <span
              className='text-xs text-destructive max-w-[200px] truncate block'
              title={errorMessage}
            >
              {errorMessage}
            </span>
          )
          : null;
      },
    },
  ];

  if (error) {
    return (
      <div className='container mx-auto py-6'>
        <Card>
          <CardHeader>
            <CardTitle>Error Loading Audit Logs</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className='container mx-auto py-6'>
      <div className='flex items-center justify-between mb-6'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>Audit Logs</h1>
          <p className='text-muted-foreground mt-2'>
            View and export system audit logs
          </p>
        </div>
        <div className='flex gap-2'>
          <Button
            variant='outline'
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className='mr-2 h-4 w-4' />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <Button
            variant='outline'
            onClick={() => handleExport('csv')}
            disabled={isExporting || events.length === 0}
          >
            <FileText className='mr-2 h-4 w-4' />
            Export CSV
          </Button>
          <Button
            variant='outline'
            onClick={() => handleExport('json')}
            disabled={isExporting || events.length === 0}
          >
            <FileJson className='mr-2 h-4 w-4' />
            Export JSON
          </Button>
        </div>
      </div>

      {showFilters && (
        <Card className='mb-6'>
          <CardHeader>
            <CardTitle>Filters</CardTitle>
            <CardDescription>
              Filter audit logs by user, action, resource, or date range
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
              <div>
                <Label htmlFor='userId'>User ID</Label>
                <Input
                  id='userId'
                  placeholder='Filter by user ID'
                  value={tempFilters.userId || ''}
                  onChange={(e) =>
                    setTempFilters({ ...tempFilters, userId: e.target.value || undefined })}
                />
              </div>
              <div>
                <Label htmlFor='username'>Username</Label>
                <Input
                  id='username'
                  placeholder='Filter by username'
                  value={tempFilters.username || ''}
                  onChange={(e) =>
                    setTempFilters({ ...tempFilters, username: e.target.value || undefined })}
                />
              </div>
              <div>
                <Label htmlFor='action'>Action</Label>
                <Input
                  id='action'
                  placeholder='e.g., CREATE_USER'
                  value={tempFilters.action || ''}
                  onChange={(e) =>
                    setTempFilters({ ...tempFilters, action: e.target.value || undefined })}
                />
              </div>
              <div>
                <Label htmlFor='resourceType'>Resource Type</Label>
                <Input
                  id='resourceType'
                  placeholder='e.g., USER'
                  value={tempFilters.resourceType || ''}
                  onChange={(e) =>
                    setTempFilters({ ...tempFilters, resourceType: e.target.value || undefined })}
                />
              </div>
              <div>
                <Label htmlFor='status'>Status</Label>
                <Select
                  value={tempFilters.status || 'all'}
                  onValueChange={(value) =>
                    setTempFilters({
                      ...tempFilters,
                      status: value === 'all' ? undefined : (value as 'success' | 'failure'),
                    })}
                >
                  <SelectTrigger id='status'>
                    <SelectValue placeholder='Select status' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>All</SelectItem>
                    <SelectItem value='success'>Success</SelectItem>
                    <SelectItem value='failure'>Failure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor='startDate'>Start Date</Label>
                <Input
                  id='startDate'
                  type='datetime-local'
                  value={tempFilters.startDate || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTempFilters({
                      ...tempFilters,
                      startDate: value ? new Date(value).toISOString() : undefined,
                    });
                  }}
                />
              </div>
              <div>
                <Label htmlFor='endDate'>End Date</Label>
                <Input
                  id='endDate'
                  type='datetime-local'
                  value={tempFilters.endDate || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTempFilters({
                      ...tempFilters,
                      endDate: value ? new Date(value).toISOString() : undefined,
                    });
                  }}
                />
              </div>
            </div>
            <div className='flex gap-2 mt-4'>
              <Button onClick={handleApplyFilters}>
                <Filter className='mr-2 h-4 w-4' />
                Apply Filters
              </Button>
              <Button variant='outline' onClick={handleClearFilters}>
                <X className='mr-2 h-4 w-4' />
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Audit Events</CardTitle>
          <CardDescription>
            {isLoading
              ? 'Loading audit logs...'
              : `${events.length} event${events.length !== 1 ? 's' : ''} found`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading
            ? (
              <div className='flex items-center justify-center py-8' role='status'>
                <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
              </div>
            )
            : (
              <>
                <DataTable
                  columns={columns}
                  data={events}
                  sorting={tableState.sorting}
                  onSortingChange={tableState.onSortingChange}
                  pagination={tableState.pagination}
                  onPaginationChange={tableState.onPaginationChange}
                />
                {nextToken && (
                  <div className='flex justify-center mt-4'>
                    <Button
                      variant='outline'
                      onClick={handleLoadMore}
                    >
                      <Download className='mr-2 h-4 w-4' />
                      Load More
                    </Button>
                  </div>
                )}
              </>
            )}
        </CardContent>
      </Card>
    </div>
  );
}
